import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import Checkbox from '../../../../../../../common/components/Checkbox/Checkbox';
import { defaultTheme } from '../../../../../../../utils/defaultTheme';
import * as SC_PA from '../../../PreferanceAnalysis.styles';
import * as SC_AW from './Awareness.styles';
import RankingTable from '../../../components/RankingTable/RankingTable';
import SimpleBarChartSection from '../../../components/SimpleBarChart/SimpleBarChart.section';
import { KPI_TYPES, SCOPE_TYPES } from '../../../../../../../common/utils/utils';
import CombinedBarLineChart from '../../../../../../../common/components/CombinedBarLineChart/CombinedBarLineChart';
import LOBLegend from '../../../components/LOBLegend/LOBLegend';
import SectionsHead from '../../../../../../../common/components/SectionsHead/SectionsHead';
import EvolutionLegend from '../../../components/EvolutionLegend/EvolutionLegend';
import { useCombinedLOBS } from '../../hooks/useCombinedLOBS';
import { TRANSLATION_TEXT } from '../../../../../../../utils/translations';
import * as TabLayout from '../../Tabs.styles';
import NoDataAvailable from '../../../../../../../common/components/NoDataAvailable/NoDataAvailable';
import { capitalizeFirstLetter } from '../../../../../../../utils/generalUtilities';
import {
    brandsObjectProcessing,
    checkedBrandsEvolutionExportFormat,
    resetBrandsListLOBLegendObject,
    SECTION_DATA_TYPE,
    sectionsHeadColor,
} from '../../utils/utils';
import TopLegend from './../../../../../../../common/components/TopLegend/TopLegend';
import { customStyleLOBLegend } from '../../Tabs.styles';
import ToggleSectionContainer from '../../../components/ToggleContainer/ToggleSection.container';
import { checkedEvolutionBrands } from '../../../data/actions';
import EvolutionCombinedChartSection from '../../../components/EvolutionCombinedChartSection/EvolutionCombinedChartSection';

const AwarenessTab = ({
    combinedChartsData,
    awarenessData,
    awarenessSectionIDs,
    rankingTableData,
    lobsAsOptions,
    simpleBarChartData,
    getBptPreferenceAnalysisAwarenessActionCreator,
    hasTopFilterOptionLoaded,
    filtersThrownError,
    appliedFilters,
    contentScope,
    evolutionBrands,
    isEvolutionEnabled,
    checkedEvolutionBrands: checkedBrands,
}) => {
    const dispatch = useDispatch();
    const evolutionCompetitionWrapperRef = useRef(null);

    const awarenessDataLoaded = () =>
        awarenessData &&
        awarenessData.hasOwnProperty(SECTION_DATA_TYPE.DEFAULT) &&
        awarenessData.sections &&
        !filtersThrownError;
    const awarenessDataForEvolutionLoaded = () =>
        awarenessData &&
        evolutionBrands &&
        isEvolutionEnabled &&
        awarenessData.hasOwnProperty(SECTION_DATA_TYPE.EVOLUTION) &&
        awarenessData.sectionsEvolutionReport &&
        !filtersThrownError;

    const [combinedChartLOBs, onLOBCheckboxChange, getVisibleLOBs] = useCombinedLOBS({
        sectionIDS: awarenessSectionIDs,
        lobsAsOptions,
    });

    //brands handling for evolution scope
    const awarenessBrandsEvolution = evolutionBrands ? brandsObjectProcessing(evolutionBrands) : [];
    const [evolutionBrandsState, setEvolutionBrandsState] = useState(awarenessBrandsEvolution);

    useEffect(() => {
        if (evolutionBrands && awarenessBrandsEvolution.length > 0) {
            setEvolutionBrandsState(
                awarenessBrandsEvolution.map(brand => {
                    const isCheckedInGlobal = checkedBrands.some(globalBrand => globalBrand.brandId === brand.key);
                    return {
                        ...brand,
                        isChecked: isCheckedInGlobal || brand.isChecked,
                    };
                })
            );
        }
    }, [evolutionBrands]);
    const onBrandsCheckboxChange = useCallback(key => {
        setEvolutionBrandsState(prev =>
            prev.map(brand => (brand.key === key ? { ...brand, isChecked: !brand.isChecked } : brand))
        );
    }, []);

    //checkboxes for sections toggle
    const [checkedSections, setCheckedSections] = useState([]);
    const onCheckboxChange = sectionID => {
        const founded = checkedSections.includes(sectionID);
        if (!founded) {
            setCheckedSections([...checkedSections, sectionID]);
        } else {
            setCheckedSections(checkedSections.filter(elem => elem !== sectionID));
        }
    };
    const lobsForRankingTable = sectionID => lobsAsOptions && lobsAsOptions[sectionID][KPI_TYPES.RANKING];

    useEffect(() => {
        if (hasTopFilterOptionLoaded) {
            getBptPreferenceAnalysisAwarenessActionCreator();
        }
    }, [hasTopFilterOptionLoaded]);

    useEffect(() => {
        setCheckedSections(awarenessSectionIDs);
    }, [awarenessSectionIDs]);

    const hasSideFiltersApplied = appliedFilters && appliedFilters?.length > 0;
    const isEvolutionScope = contentScope === SCOPE_TYPES.BPT_EVOLUTION && isEvolutionEnabled;

    useEffect(() => {
        if (evolutionBrandsState.length > 0) {
            dispatch(
                checkedEvolutionBrands(isEvolutionScope ? checkedBrandsEvolutionExportFormat(evolutionBrandsState) : [])
            );
        }
    }, [isEvolutionScope, evolutionBrandsState, setEvolutionBrandsState]);

    const checkSectionDataLoadedBasedOnScope = isEvolutionScope
        ? awarenessDataForEvolutionLoaded()
        : awarenessDataLoaded();

    const awarenessSectionsSorted = awarenessData?.sections?.sort((a, b) => a.sectionOrder - b.sectionOrder);
    const awarenessSectionsEvolutionSorted = awarenessData?.sectionsEvolutionReport?.sort(
        (a, b) => a.sectionOrder - b.sectionOrder
    );
    return (
        <>
            {checkSectionDataLoadedBasedOnScope && (
                <SC_AW.SectionsBannerWrapper data-testid={'Awareness-Tab-Section-Toggle-Sections'}>
                    {awarenessData?.[isEvolutionScope ? SECTION_DATA_TYPE.EVOLUTION : SECTION_DATA_TYPE.DEFAULT]?.map(
                        section => (
                            <Checkbox
                                key={section.sectionID}
                                value={checkedSections.includes(section.sectionID)}
                                label={capitalizeFirstLetter(section.sectionLabel)}
                                onClick={() => onCheckboxChange(section.sectionID)}
                                padding="6px 0px"
                                textColor={defaultTheme.blue[900]}
                            />
                        )
                    )}
                </SC_AW.SectionsBannerWrapper>
            )}
            {!filtersThrownError && (
                <TopLegend
                    customStyle={{ _wrapper: { marginTop: '1.5rem' } }}
                    rankingLegendIsVisible={!isEvolutionScope}
                />
            )}
            {!filtersThrownError && hasSideFiltersApplied && <EvolutionLegend />}
            {!filtersThrownError && isEvolutionEnabled && <ToggleSectionContainer />}
            {isEvolutionScope && evolutionBrands && (
                <div style={{ padding: '0 65px 25px' }}>
                    <LOBLegend
                        LOBs={evolutionBrandsState}
                        sectionID={1}
                        onChangeHandler={e => onBrandsCheckboxChange(e)}
                        styleObject={{
                            _optionGroupWrapper: {
                                ...customStyleLOBLegend,
                                justifyContent: 'flex-start',
                                alignContent: 'space-evenly',
                                alignItems: 'baseline',
                                minHeight: '55px',
                                padding: '10px 50px',
                            },
                            _legendWrapper: { width: '100%' },
                            _optionWrapper: { minWidth: '140px' },
                        }}
                        globalSelectionProps={null}
                        resetSelectionsProps={{
                            ...resetBrandsListLOBLegendObject(evolutionBrandsState, setEvolutionBrandsState),
                        }}
                        hasGlobalSelectionSection={false}
                        isEvolutionReport={true}
                    />
                </div>
            )}

            {!isEvolutionScope ? (
                <>
                    {awarenessDataLoaded() ? (
                        <>
                            {awarenessSectionsSorted.map(
                                ({ sectionID, sectionLabel }) =>
                                    checkedSections.includes(sectionID) && (
                                        <SectionsHead
                                            key={sectionID}
                                            title={sectionLabel}
                                            isResponsive={false}
                                            customStyle={sectionsHeadColor}
                                        >
                                            <TabLayout.SectionRow style={{ margin: '70px 0px 0px 0px' }}>
                                                <TabLayout.SectionItem
                                                    data-testid={'Awareness-Tab-Section-Item-Simple-Charts'}
                                                >
                                                    {simpleBarChartData[sectionID] && (
                                                        <SimpleBarChartSection
                                                            barChartDataYear={simpleBarChartData[sectionID].year}
                                                            barChartDataPeriod={simpleBarChartData[sectionID].period}
                                                            hasNumberOfRespondents={true}
                                                        />
                                                    )}
                                                </TabLayout.SectionItem>
                                                <TabLayout.SectionItem
                                                    data-testid={'Awareness-Tab-Section-Item-No-Dropdown-Ranking'}
                                                >
                                                    {rankingTableData[sectionID] &&
                                                        rankingTableData[sectionID].length > 0 && (
                                                            <RankingTable
                                                                rankingData={[rankingTableData[sectionID][0]]}
                                                                lobOptions={lobsForRankingTable(sectionID)}
                                                                styleObject={{ margin: '-40px 0px 40px 20px' }}
                                                                hasNumberOfRespondents={true}
                                                                hasSideFiltersApplied={hasSideFiltersApplied}
                                                            />
                                                        )}
                                                </TabLayout.SectionItem>
                                            </TabLayout.SectionRow>
                                            <TabLayout.SectionRow>
                                                <TabLayout.SectionItem
                                                    data-testid={'Awareness-Tab-Section-Item-Combined-Charts'}
                                                >
                                                    {combinedChartsData && combinedChartLOBs ? (
                                                        <SC_PA.LOBAreaWrapper>
                                                            <SC_PA.CombinedChartsWrapper>
                                                                {combinedChartsData[sectionID]?.map(
                                                                    d =>
                                                                        d.lineData.length > 0 &&
                                                                        Object.keys(d.barData).length > 0 && (
                                                                            <CombinedBarLineChart
                                                                                key={sectionID + d.chartTitle}
                                                                                visibleLOBs={getVisibleLOBs(sectionID)}
                                                                                lineData={d.lineData}
                                                                                barData={d.barData}
                                                                                customLineTooltip={{
                                                                                    tooltipFontColor:
                                                                                        defaultTheme.blue[900],
                                                                                }}
                                                                            />
                                                                        )
                                                                )}
                                                            </SC_PA.CombinedChartsWrapper>
                                                            {combinedChartLOBs[sectionID][KPI_TYPES.COMBINED].length >
                                                            0 ? (
                                                                <LOBLegend
                                                                    LOBs={
                                                                        combinedChartLOBs[sectionID][KPI_TYPES.COMBINED]
                                                                    }
                                                                    sectionID={sectionID}
                                                                    onChangeHandler={onLOBCheckboxChange}
                                                                />
                                                            ) : (
                                                                <SC_PA.NoDataText>
                                                                    {TRANSLATION_TEXT.NO_DATA_AVAILABLE_TEXT}
                                                                </SC_PA.NoDataText>
                                                            )}
                                                        </SC_PA.LOBAreaWrapper>
                                                    ) : null}
                                                </TabLayout.SectionItem>
                                                <TabLayout.SectionItem
                                                    data-testid={'Awareness-Tab-Section-Item-Dropdown-Ranking'}
                                                >
                                                    {rankingTableData[sectionID] &&
                                                        rankingTableData[sectionID].length > 0 && (
                                                            <RankingTable
                                                                noTitle={true}
                                                                rankingData={[rankingTableData[sectionID][1]]}
                                                                lobOptions={lobsForRankingTable(sectionID)}
                                                                styleObject={{ margin: '0px 0px 40px 20px' }}
                                                                hasNumberOfRespondents={true}
                                                                hasSideFiltersApplied={hasSideFiltersApplied}
                                                            />
                                                        )}
                                                </TabLayout.SectionItem>
                                            </TabLayout.SectionRow>
                                        </SectionsHead>
                                    )
                            )}
                        </>
                    ) : (
                        <NoDataAvailable show={!awarenessDataLoaded() && !filtersThrownError} />
                    )}
                </>
            ) : (
                <>
                    {awarenessDataForEvolutionLoaded() ? (
                        <>
                            {awarenessSectionsEvolutionSorted.map(
                                ({ sectionID, sectionLabel }) =>
                                    checkedSections.includes(sectionID) && (
                                        <SectionsHead
                                            key={sectionID}
                                            title={sectionLabel}
                                            isResponsive={false}
                                            customStyle={sectionsHeadColor}
                                        >
                                            <TabLayout.SectionRow ref={evolutionCompetitionWrapperRef}>
                                                <TabLayout.SectionItem>
                                                    {combinedChartsData ? (
                                                        <EvolutionCombinedChartSection
                                                            sectionID={sectionID}
                                                            legendItems={evolutionBrandsState}
                                                            combinedChartsData={combinedChartsData}
                                                            wrapperRef={evolutionCompetitionWrapperRef}
                                                        />
                                                    ) : (
                                                        <SC_PA.NoDataText>
                                                            {TRANSLATION_TEXT.NO_DATA_AVAILABLE_TEXT}
                                                        </SC_PA.NoDataText>
                                                    )}
                                                </TabLayout.SectionItem>
                                            </TabLayout.SectionRow>
                                        </SectionsHead>
                                    )
                            )}
                        </>
                    ) : (
                        <NoDataAvailable show={!awarenessDataForEvolutionLoaded() && !filtersThrownError} />
                    )}
                </>
            )}
        </>
    );
};

export default AwarenessTab;
